import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { SessionFacade, DealFacade } from '@ipreo/muni-shared';
import {
  WebsocketGatewayService,
  WebsocketGatewayAuthType
} from '@ipreo/northstar';

@Component({
  standalone: false,
  selector: 'gameday-direct-app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  public readonly isAuthorized: Observable<
    boolean
  > = this.sessionFacade.session$.pipe(map(() => true));

  public readonly isLoading: Observable<
    boolean
  > = this.dealFacade.dealState$.pipe(map(dealState => dealState.loading));

  public readonly username: Observable<
    string
  > = this.sessionFacade.session$.pipe(map(session => session.name));

  public currentFooterLabel = 'Confidential';
  public showClassificationModal = false;
  public footerLinks: Array<{ name: string; href?: string; openInNewTab?: boolean }>;

  private routerSub: Subscription;

  constructor(
    private sessionFacade: SessionFacade,
    private dealFacade: DealFacade,
    private websocketGatewayService: WebsocketGatewayService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  public ngOnInit() {
    this.sessionFacade.session$
      .pipe(
        filter(session => !!session.websocketGatewaySession),
        map(session => session.websocketGatewaySession.token),
        distinctUntilChanged()
      )
      .subscribe(token => {
        this.websocketGatewayService.connect(
          WebsocketGatewayAuthType.JWT,
          token
        );
      });

    this.initializeFooterLinks();

    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateFooterLinks();
      });
  }

  public ngAfterViewInit() {
    setTimeout(() => {
      document.addEventListener('click', (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const link = target.closest('a[href="#open-classification-notice"]');
        if (link) {
          event.preventDefault();
          event.stopPropagation();
          this.showClassificationNotice();
          this.cdr.detectChanges();
        }
      }, true);
    }, 100);
  }

  public ngOnDestroy(): void {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }

  public showClassificationNotice() {
    if (this.showClassificationModal) {
      return;
    }
    this.showClassificationModal = true;
  }

  public closeClassificationModal() {
    if (!this.showClassificationModal) {
      return;
    }
    this.showClassificationModal = false;
  }

  private initializeFooterLinks() {
    this.footerLinks = [
      {
        name: 'Corporate Privacy Policy',
        href: 'https://www.spglobal.com/en/privacy/privacy-policy-english',
        openInNewTab: true,
      },
      {
        name: 'Terms of Use',
        href: 'https://www.spglobal.com/en/terms-of-use',
        openInNewTab: true,
      },
      {
        name: 'Cookie Notice',
        href: 'https://www.spglobal.com/en/cookie-notice',
        openInNewTab: true,
      },
      {
        name: 'Client Privacy Portal',
        href: 'https://www.spglobal.com/en/enterprise/Legal/privacy.html',
        openInNewTab: true,
      },
      {
        name: 'Do Not Sell my Personal Information',
        href: 'https://more.spglobal.com/DoNotSell',
        openInNewTab: true,
      },
      {
        name: this.currentFooterLabel,
        href: '#open-classification-notice',
      },
    ];
  }

  private updateFooterLinks() {
    if (this.footerLinks && this.footerLinks.length > 0) {
      this.footerLinks = [
        ...this.footerLinks.slice(0, -1),
        {
          name: this.currentFooterLabel,
          href: '#open-classification-notice',
        },
      ];
    }
  }
}
